import { PayloadAction, createAsyncThunk, createSelector, createSlice } from "@reduxjs/toolkit";
import jwtDecode from "jwt-decode";
import { APIError, AppThunkAPIType } from "../../app/appTypes";
import { RootState } from "../../app/store";
import { get, getWithAuth, post } from "../../http";
import { Role } from "../users/usersSlice";

export type TokenState
    = { tag: "empty", reason?: "logout" }
    | { tag: "valid", token: string, timeLeft?: number  }
    | { tag: "invalid", token: string }

export interface UserState {
    tokenState: TokenState | undefined;
}

interface JWTToken {
    unique_name: string,
    sub: string,
    role: string,
    companyId: string
    companyName: string
    nameid: string
}

const initialState: UserState = {
    tokenState: undefined,
}

export interface Credentials {
    username: string;
    password: string;
}

export interface LoginResponse {
    token: string,
    requires2FA: boolean;
    emailFor2FA: string;
    passwordExpired: boolean;
}


export interface TwoFAResponse {
    requires2FA: boolean;
    emailFor2FA: string;
}

export interface Verify2FaRequest {
    email: string;
    code: string;
}

export interface Resend2FaRequest {
    email: string;
}

export interface AccountConfirmation {
    user: string;
    password: string;
    passwordConfirmation: string;
    token: string;
}

export interface ResetPasswordType {
    email: string;
    password: string;
    passwordConfirmation: string;
    token: string;
}

export interface ChangePasswordType {
    email: string;
    oldPassword: string;
    newPassword: string;
}

export interface ForgotPasswordType {
    email: string;
}

export const loadTokenFromStorage = createAsyncThunk<TokenState | undefined, void, AppThunkAPIType>("user/loadTokenFromStorage", async (_, api) => {
    const { dispatch, rejectWithValue } = api
    const token: string | null = localStorage.getItem("token");

    if (token) {
        try {
            const { message } = await dispatch(validateToken(token)).unwrap()
            return {
                token,
                tag: message === 'Valid' ? 'valid' : 'invalid'
            }
        } catch (error) {
            return {
                token,
                tag: 'invalid',
            }
        }
    } else {
        return rejectWithValue({
            kind: 'unknown'
        })
    }
})

export const logout = createAsyncThunk<void, void, AppThunkAPIType>("user/logout", async () => {
    localStorage.removeItem("token");
})

export const login = createAsyncThunk<LoginResponse, Credentials, { rejectValue: APIError }>('user/login', async (c: Credentials, thunkAPI) => {
    try {
        const response = await post("api/AuthManagement/Login", c);

        if (response.ok) {
            const data = await response.json();

            if (data.requires2FA || data.passwordExpired) {
                return data;
            }

            if (data.token && !data.requires2FA) {
                localStorage.setItem("token", data.token);
                return data;
            }
        }

        return thunkAPI.rejectWithValue({
            kind: "http",
            status: response.status,
            body: await response.text(),
        });

    } catch (error) {
        return thunkAPI.rejectWithValue({
            kind: 'connection',
        })
    }
})


export interface RegisterPayload {
    email: string;
    emailConfirmation: string;
    companyName: string;
    phoneNumber?: string;
}

export interface SsoRegisterPayload {
    registrationId: string;
    email: string;
    password: string;
    passwordConfirmation: string;
}

export interface ConfirmSsoRegistrationPayload {
    userId: string;
    token: string;
}

export const register = createAsyncThunk<void, RegisterPayload, { rejectValue: APIError }>('user/register', async (c: RegisterPayload, thunkAPI) => {
    try {
        const response = await post("api/AuthManagement/register", c);

        if (response.ok) {
            return;
        }

        return thunkAPI.rejectWithValue({
            kind: "http",
            status: response.status,
            body: await response.text(),
        });

    } catch (error) {
        return thunkAPI.rejectWithValue({
            kind: 'connection',
        })
    }
})

export const registerSsoUser = createAsyncThunk<void, SsoRegisterPayload, { rejectValue: APIError }>('user/register-sso', async (payload: SsoRegisterPayload, thunkAPI) => {
    try {
        const response = await post('api/AuthManagement/complete-sso-registration', payload);

        if (response.ok) {
            return;
        }

        return thunkAPI.rejectWithValue({
            kind: 'http',
            status: response.status,
            body: await response.text(),
        });
    } catch (error) {
        return thunkAPI.rejectWithValue({
            kind: 'connection',
        });
    }
})

export const confirmSsoRegistration = createAsyncThunk<void, ConfirmSsoRegistrationPayload, { rejectValue: APIError }>('user/confirmSsoRegistration', async (payload: ConfirmSsoRegistrationPayload, thunkAPI) => {
    try {
        const response = await post('api/AuthManagement/confirm-sso-registration', payload);

        if (response.ok) {
            return;
        }

        return thunkAPI.rejectWithValue({
            kind: 'http',
            status: response.status,
            body: await response.text(),
        });
    } catch (error) {
        return thunkAPI.rejectWithValue({
            kind: 'connection',
        });
    }
})


export const fetchSSOToken = createAsyncThunk<LoginResponse, string, { rejectValue: APIError }>('user/sso-token', async (guid, thunkAPI) => {
    try {

        const response = await fetch(process.env.REACT_APP_REST_API + `api/AuthManagement/sso-token/${guid}`, {
            method: "GET",
            mode: "cors",
            cache: "no-cache",
            credentials: "same-origin",
            headers: {
                'Content-Type': "application/json",
            },
            redirect: "manual",

            referrerPolicy: "no-referrer",
        })


        if (response.ok) {
            const data = await response.json();

            if (data.token) {
                localStorage.setItem("token", data.token);
                return data;
            }
        }

        return thunkAPI.rejectWithValue({
            kind: "http",
            status: response.status,
            body: await response.text(),
        });

    } catch (error) {
        return thunkAPI.rejectWithValue({
            kind: 'connection',
        })
    }
})


export const verify2FA = createAsyncThunk<string, Verify2FaRequest, { rejectValue: APIError }>('user/verify2FA', async (p: Verify2FaRequest, thunkAPI) => {
    try {
        const response = await post("api/AuthManagement/verify-2fa", p);

        if (response.ok) {
            const data = await response.json();

            if (data.token) {
                localStorage.setItem("token", data.token);
                return data.token;
            }
        }

        return thunkAPI.rejectWithValue({
            kind: "http",
            status: response.status,
            body: await response.text(),
        });

    } catch (error) {
        return thunkAPI.rejectWithValue({
            kind: 'connection',
        })
    }
})

export const resend2FA = createAsyncThunk<void, Resend2FaRequest, { rejectValue: APIError }>('user/resend2FA', async (p: Resend2FaRequest, thunkAPI) => {
    try {
        const response = await post("api/AuthManagement/resend-2fa", p);

        if (response.ok) {
            return;
        }

        return thunkAPI.rejectWithValue({
            kind: "http",
            status: response.status,
            body: await response.text(),
        });

    } catch (error) {
        return thunkAPI.rejectWithValue({
            kind: 'connection',
        })
    }
})

export const confirmAccount = createAsyncThunk<string, AccountConfirmation, { rejectValue: APIError }>('user/accountConfirmation', async (c: AccountConfirmation, thunkAPI) => {
    try {
        const response = await post("api/AuthManagement/ConfirmAccount", c);

        if (response.ok) {


            return "";
        }

        return thunkAPI.rejectWithValue({
            kind: "http",
            status: response.status,
            body: await response.text(),
        });

    } catch (error) {
        return thunkAPI.rejectWithValue({
            kind: 'connection',
        })
    }
})

export const resetPassword = createAsyncThunk<string, ResetPasswordType, { rejectValue: APIError }>
    ('user/resetpassword', async (resetPassword: ResetPasswordType, thunkAPI) => {
        try {
            const response = await post("api/AuthManagement/reset-password", resetPassword);

            if (response.ok) {
                return "";
            }

            return thunkAPI.rejectWithValue({
                kind: "http",
                status: response.status,
                body: await response.text(),
            });

        } catch (error) {
            return thunkAPI.rejectWithValue({
                kind: 'connection',
            })
        }
    })

export const changePassword = createAsyncThunk<string, ChangePasswordType, { rejectValue: APIError }>
    ('user/change-password', async (changePassword: ChangePasswordType, thunkAPI) => {
        try {
            const response = await post("api/AuthManagement/change-password", changePassword);

            if (response.ok) {
                return "";
            }

            return thunkAPI.rejectWithValue({
                kind: "http",
                status: response.status,
                body: await response.text(),
            });

        } catch (error) {
            return thunkAPI.rejectWithValue({
                kind: 'connection',
            })
        }
    })

export const forgotPassword = createAsyncThunk<string, ForgotPasswordType, { rejectValue: APIError }>
    ('user/forgotPassword', async (forgotPassword: ForgotPasswordType, thunkAPI) => {
        try {
            const response = await post("api/AuthManagement/forgot-password", forgotPassword);

            if (response.ok) {
                return "";
            }

            return thunkAPI.rejectWithValue({
                kind: "http",
                status: response.status,
                body: await response.text(),
            });

        } catch (error) {
            return thunkAPI.rejectWithValue({
                kind: 'connection',
            })
        }
    })

export const refreshToken = createAsyncThunk<string | undefined, void, AppThunkAPIType>('user/refresh-token', async (_, { dispatch, rejectWithValue }) => {
    const result = await dispatch(getWithAuth({ url: "api/AuthManagement/refresh-token" }))
    const { payload } = result
    if (getWithAuth.fulfilled.match(result)) {
        if (payload.token) {
            localStorage.setItem("token", payload.token);
            return payload.token
        } else {
            return undefined
        }
    } else {
        return rejectWithValue(payload ?? { kind: 'unknown' })
    }
})

export interface TokenValidationResult {
    message: string
    expiresAt: string
    timeLeftInSeconds: number
}
export const validateToken = createAsyncThunk<TokenValidationResult, string, AppThunkAPIType>('user/validate-token', async (token, { dispatch, rejectWithValue }) => {
    const headers = {
        'Authorization': `Bearer ${token ?? 'null'}`,
    }
    const result = await dispatch(get({ url: "api/AuthManagement/validate", headers }))
    const { payload } = result
    if (get.fulfilled.match(result)) {
        return payload as TokenValidationResult
    } else {
        return rejectWithValue(payload ?? { kind: 'unknown' })
    }
})

export const userSlice = createSlice({
    name: 'user',
    initialState,
    reducers: {
        invalidateToken: state => {
            if (state.tokenState) {
                switch (state.tokenState.tag) {
                    case 'valid':
                        state.tokenState = { tag: 'invalid', token: state.tokenState.token }
                        break;
                }
            }
        },
    },
    extraReducers: (builder) => {
        builder.addCase(login.fulfilled, (state, action) => {
            if (action.payload.token && !action.payload.requires2FA) {
                state.tokenState = { tag: 'valid', token: action.payload.token }
            }
        });

        builder.addCase(verify2FA.fulfilled, (state, action) => {
            state.tokenState = { tag: 'valid', token: action.payload }
        });

        builder.addCase(fetchSSOToken.fulfilled, (state, action) => {
            if (action.payload.token) {
                state.tokenState = { tag: 'valid', token: action.payload.token }
            }
        });

        builder.addCase(logout.fulfilled, (state) => {
            state.tokenState = { tag: 'empty', reason: 'logout' };
        });
        builder.addCase(loadTokenFromStorage.fulfilled, (state, action) => {
            state.tokenState = action.payload
        });
        builder.addCase(loadTokenFromStorage.rejected, (state) => {
            state.tokenState = { tag: "empty" }
        });
        builder.addCase(refreshToken.fulfilled, (state, action) => {
            if (action.payload) {
                state.tokenState = { tag: 'valid', token: action.payload }
            } else {
                state.tokenState = { tag: 'empty' }
            }
        })
        builder.addCase(refreshToken.rejected, (state, _) => {
            state.tokenState = { tag: 'empty' }
        })
        builder.addCase(validateToken.rejected, (state, _) => {
            if (state.tokenState && state.tokenState.tag === 'valid') {
                state.tokenState = { tag: 'invalid', token: state.tokenState.token }
            }
        })
        builder.addCase(validateToken.fulfilled, (state, action) => {
            const { tokenState } = state
            if (tokenState && tokenState.tag === 'valid') {
                state.tokenState = { ...tokenState, timeLeft: action.payload.timeLeftInSeconds }
            }
        })
    },
})

export const { invalidateToken } = userSlice.actions

export const selectUser = (state: RootState) => state.user

export const selectIsTokenValid = (state: RootState) => selectUser(state).tokenState?.tag === "valid"

const selectToken = (state: RootState) => state.user.tokenState && state.user.tokenState.tag === 'valid' ? state.user.tokenState.token : ''

export const selectBearerToken = createSelector(selectUser, user => {
    const token = user.tokenState && user.tokenState.tag === 'valid' ? user.tokenState.token : ''
    return {
        'Authorization': `Bearer ${token ?? 'null'}`,
    }
})

export const selectLoggedUser = createSelector(selectToken, token => {
    if (token) {
        const { sub }: JWTToken = jwtDecode(token)
        return sub
    }

    return ""
})

export const selectUserRole = createSelector(selectToken, token => {
    if (token) {
        const { role }: JWTToken = jwtDecode(token)
        return role as Role
    }

    return []
})

export const selectUserCompanyId = createSelector(selectToken, token => {
    if (token) {
        const { companyId }: JWTToken = jwtDecode(token)
        return companyId
    }

    return null
})

export const selectUserCompanyName = createSelector(selectToken, token => {
    if (token) {
        const { companyName }: JWTToken = jwtDecode(token)
        return companyName
    }

    return null
})

export const selectLoggedUserId = createSelector(selectToken, token => {
    if (token) {
        const { nameid }: JWTToken = jwtDecode(token)
        return nameid
    }

    return ""
})

export default userSlice.reducer
